/**
 * BackofficeProtectedRoute - Security wrapper for admin panel
 * Shows fake 404 for unauthorized users for security
 * Add ?debug=true to URL to see actual errors
 */

import { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { getApiUrl, getEnvironment } from '../config/appConfig';

// Use auto-detected API URL from config
const getAPI = () => getApiUrl();

export default function BackofficeProtectedRoute({ children }) {
  const [status, setStatus] = useState('loading'); // loading, authorized, unauthorized, not_internal, error
  const [errorMessage, setErrorMessage] = useState('');
  const [debugInfo, setDebugInfo] = useState(null);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isDebugMode = searchParams.get('debug') === 'true';

  useEffect(() => {
    const validateBackofficeAccess = async () => {
      const API = getAPI();
      try {
        // First check if user is logged in
        console.log('[BackofficeProtectedRoute] Checking auth at:', `${API}/auth/me`);
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        const user = userRes.data;
        console.log('[BackofficeProtectedRoute] User:', user?.email, 'Role:', user?.role);
        
        setDebugInfo(prev => ({ ...prev, user: user?.email, role: user?.role }));

        if (!user) {
          console.log('[BackofficeProtectedRoute] No user found');
          setStatus('unauthorized');
          setErrorMessage('Please log in to access this area.');
          setDebugInfo(prev => ({ ...prev, error: 'No user in response' }));
          return;
        }

        // Check if user is an internal user (backoffice staff) or legacy super_admin
        const isInternalUser = user.is_backoffice_user || 
                               user.is_internal_user || 
                               (user.user_id && user.user_id.startsWith('int_')) ||
                               user.role === 'super_admin' ||
                               user.admin_role === 'super_admin';

        console.log('[BackofficeProtectedRoute] Is internal user:', isInternalUser, {
          is_backoffice_user: user.is_backoffice_user,
          is_internal_user: user.is_internal_user,
          user_id: user.user_id,
          role: user.role,
          admin_role: user.admin_role
        });
        
        setDebugInfo(prev => ({ ...prev, isInternalUser, checks: {
          is_backoffice_user: user.is_backoffice_user,
          is_internal_user: user.is_internal_user,
          user_id_starts_int: user.user_id?.startsWith('int_'),
          role: user.role,
          admin_role: user.admin_role
        }}));

        if (!isInternalUser) {
          setStatus('not_internal');
          setErrorMessage('Access denied. This area is restricted to internal staff only.');
          return;
        }

        // Validate admin access with backend
        console.log('[BackofficeProtectedRoute] Validating backoffice access...');
        const accessRes = await axios.post(`${API}/backoffice/validate-access`, {}, { withCredentials: true });
        console.log('[BackofficeProtectedRoute] Access response:', accessRes.data);
        
        setDebugInfo(prev => ({ ...prev, accessResponse: accessRes.data }));
        
        if (!accessRes.data.has_access) {
          setStatus('unauthorized');
          setErrorMessage(accessRes.data.reason || 'Access denied.');
          return;
        }

        // All checks passed
        console.log('[BackofficeProtectedRoute] Access granted');
        setStatus('authorized');
      } catch (error) {
        console.error('[BackofficeProtectedRoute] Error:', error.response?.status, error.response?.data || error.message);
        
        setDebugInfo(prev => ({ 
          ...prev, 
          error: error.message,
          status: error.response?.status,
          data: error.response?.data
        }));
        
        // If 401/403, user is not authenticated
        if (error.response?.status === 401 || error.response?.status === 403) {
          setStatus('unauthorized');
          setErrorMessage('Session expired. Please log in again.');
        } else {
          // For other errors (network issues, etc.)
          setStatus('error');
          setErrorMessage(error.message || 'Unable to verify access. Please try again.');
        }
      }
    };

    validateBackofficeAccess();
  }, [location.pathname]);

  // Loading state
  if (status === 'loading') {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-slate-900">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500 mx-auto mb-4"></div>
            <p className="text-slate-400">Verifying access...</p>
          </div>
        </div>
      </>
    );
  }

  // Debug mode - show actual error info
  if (isDebugMode && (status === 'not_internal' || status === 'unauthorized' || status === 'error')) {
    return (
      <div className="min-h-screen bg-slate-900 p-8">
        <div className="max-w-2xl mx-auto bg-slate-800 rounded-lg p-6">
          <h1 className="text-2xl font-bold text-red-500 mb-4">Access Debug Info</h1>
          <div className="space-y-4">
            <div>
              <span className="text-slate-400">Status:</span>
              <span className="text-white ml-2">{status}</span>
            </div>
            <div>
              <span className="text-slate-400">Error:</span>
              <span className="text-white ml-2">{errorMessage}</span>
            </div>
            <div>
              <span className="text-slate-400">Environment:</span>
              <span className="text-white ml-2">{getEnvironment()}</span>
            </div>
            <div>
              <span className="text-slate-400">API URL:</span>
              <span className="text-white ml-2">{getAPI()}</span>
            </div>
            <div>
              <span className="text-slate-400">Debug Info:</span>
              <pre className="text-green-400 mt-2 bg-slate-900 p-4 rounded overflow-auto text-sm">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
            <div className="flex gap-4 mt-6">
              <a href="/login" className="px-4 py-2 bg-violet-600 text-white rounded hover:bg-violet-700">
                Go to Login
              </a>
              <a href="/" className="px-4 py-2 border border-slate-600 text-white rounded hover:bg-slate-700">
                Go Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not an internal user - show 404 (disguise as not found)
  if (status === 'not_internal' || status === 'unauthorized' || status === 'error') {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>404 - Page Not Found</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-slate-900">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="text-8xl font-bold text-slate-700 mb-4">404</div>
            <h1 className="text-2xl font-bold text-white mb-2">Page Not Found</h1>
            <p className="text-slate-400 mb-8">
              The page you&apos;re looking for doesn&apos;t exist or has been moved.
            </p>
            <div className="flex gap-4 justify-center">
              <a 
                href="/" 
                className="inline-flex items-center justify-center px-6 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
              >
                Go to Homepage
              </a>
              <a 
                href="/login" 
                className="inline-flex items-center justify-center px-6 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-800 transition-colors"
              >
                Sign In
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Authorized - render children with SEO protection
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {children}
    </>
  );
}
